import { isExperimentArm, randomArm } from "./experiment";
import type { CurrencyCode, ExperimentArm, PersistedDemoState, PotRecord, SplitResult } from "./types";

const STORAGE_KEY = "money-rituals:phase0:v1";
const SESSION_MARKER_KEY = "money-rituals:phase0:active-participant";
const MAX_NICKNAME_LENGTH = 20;
let armToPersist: ExperimentArm | null = null;
let persistenceDisabled = false;

export interface DemoSession {
  state: PersistedDemoState;
  isReturnSession: boolean;
  previousSessionAt?: string;
}

export function openDemoSession(
  search: URLSearchParams,
  testMode = false,
  currencyCode: CurrencyCode = "USD",
): DemoSession {
  persistenceDisabled = testMode;
  if (search.get("reset") === "1") {
    removeStoredState();
  }

  const stored = readState(currencyCode);
  const forcedArm = search.get("arm");
  const now = new Date().toISOString();
  const persistentArm = stored?.arm ?? randomArm();
  armToPersist = persistentArm;
  const arm: ExperimentArm = isExperimentArm(forcedArm) ? forcedArm : persistentArm;
  const continuingTab = Boolean(stored && readSessionMarker() === stored.participantId);
  const previousSessionAt = stored && !continuingTab ? stored.lastSessionAt : undefined;

  const state: PersistedDemoState = stored
    ? {
        ...stored,
        arm,
        sessionCount: continuingTab ? stored.sessionCount : stored.sessionCount + 1,
        lastSessionAt: continuingTab ? stored.lastSessionAt : now,
      }
    : {
        version: 1,
        participantId: createId("p"),
        arm,
        currencyCode,
        sessionCount: 1,
        lastSessionAt: now,
        potCounter: 0,
        confirmedKeepCents: 0,
        confirmedKeepCount: 0,
        pots: [],
      };

  if (!testMode) writeSessionMarker(state.participantId);
  saveState(state);
  return {
    state,
    isReturnSession: Boolean(stored && !continuingTab),
    previousSessionAt,
  };
}

export function createPot(state: PersistedDemoState, amountCents: number, band: string): PotRecord {
  state.potCounter += 1;
  const pot: PotRecord = {
    id: `pot-${state.potCounter}-${createId("r")}`,
    amountCents,
    amountBand: band,
    createdAt: new Date().toISOString(),
  };
  state.pots.push(pot);
  state.pots = state.pots.slice(-20);
  saveState(state);
  return pot;
}

export function setPotSplit(
  state: PersistedDemoState,
  potId: string,
  split: SplitResult,
  selectedBubble: number,
): PotRecord {
  const pot = requirePot(state, potId);
  if (pot.split) {
    throw new Error("This ritual pot already has a split.");
  }
  pot.split = split;
  pot.selectedBubble = selectedBubble;
  saveState(state);
  return pot;
}

export function setPotKeepDecision(
  state: PersistedDemoState,
  potId: string,
  confirmed: boolean,
): PotRecord {
  const pot = requirePot(state, potId);
  if (!pot.split) {
    throw new Error("Complete the Frog ritual before recording a keep decision.");
  }
  if (pot.keepConfirmed !== undefined) {
    throw new Error("This ritual pot already has a keep decision.");
  }
  pot.keepConfirmed = confirmed;
  if (confirmed) {
    pot.keepConfirmedAt = new Date().toISOString();
    state.confirmedKeepCents += pot.split.keepCents;
    state.confirmedKeepCount += 1;
  }
  saveState(state);
  return pot;
}

export function setPotWish(state: PersistedDemoState, potId: string, messageId: string): PotRecord {
  const pot = requirePot(state, potId);
  if (!pot.wishMessageId) {
    pot.wishMessageId = messageId;
    saveState(state);
  }
  return pot;
}

export function setNickname(state: PersistedDemoState, rawNickname: string): string | null {
  const nickname = normalizeNickname(rawNickname);
  if (nickname) {
    state.nickname = nickname;
  } else {
    delete state.nickname;
  }
  saveState(state);
  return nickname;
}

export function persistState(state: PersistedDemoState): void {
  saveState(state);
}

export function clearDemoState(): void {
  removeStoredState();
  try {
    sessionStorage.removeItem(SESSION_MARKER_KEY);
  } catch {
    // A storage failure must not block local reset.
  }
}

function readState(currencyCode: CurrencyCode): PersistedDemoState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedDemoState>;
    const parsedArm = parsed.arm;
    if (
      parsed.version !== 1 ||
      typeof parsed.participantId !== "string" ||
      !/^p_[0-9a-f]{20}$/.test(parsed.participantId) ||
      !isExperimentArm(parsedArm)
    ) {
      return null;
    }
    const confirmedKeepCount = safeNonNegativeInteger(parsed.confirmedKeepCount);
    const confirmedKeepCents = safeNonNegativeInteger(parsed.confirmedKeepCents);
    // Builds before v0.5 stored USD values without a currency marker. Treat that
    // legacy shape as USD, and clear only currency-denominated totals when the
    // resolved currency changes so different minor units are never combined.
    const storedCurrency = parsed.currencyCode === undefined
      ? "USD"
      : isCurrencyCode(parsed.currencyCode)
        ? parsed.currencyCode
        : null;
    const currencyMatches = storedCurrency === currencyCode;
    const hasCoherentKeepTotal =
      (confirmedKeepCount === 0 && confirmedKeepCents === 0) ||
      (confirmedKeepCount > 0 && confirmedKeepCents > 0);
    const nickname = normalizeNickname(parsed.nickname);
    return {
      version: 1,
      participantId: parsed.participantId,
      arm: parsedArm,
      currencyCode,
      ...(nickname ? { nickname } : {}),
      sessionCount: safePositiveInteger(parsed.sessionCount, 1),
      lastSessionAt: validIsoDate(parsed.lastSessionAt) ? parsed.lastSessionAt : new Date().toISOString(),
      potCounter: safeNonNegativeInteger(parsed.potCounter),
      confirmedKeepCents: currencyMatches && hasCoherentKeepTotal ? confirmedKeepCents : 0,
      confirmedKeepCount: currencyMatches && hasCoherentKeepTotal ? confirmedKeepCount : 0,
      // Durable snapshots never need per-pot records. Discarding legacy values
      // also migrates early builds that persisted exact pots.
      pots: [],
    };
  } catch {
    return null;
  }
}

function saveState(state: PersistedDemoState): void {
  if (persistenceDisabled) return;
  try {
    const durableSnapshot: PersistedDemoState = {
      ...state,
      arm: armToPersist ?? state.arm,
      // Exact pot and split values are session-only. Confirmed totals persist only
      // after the explicit "I set it aside" action.
      pots: [],
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(durableSnapshot));
  } catch {
    // The ritual remains usable when storage is unavailable or full.
  }
}

function readSessionMarker(): string | null {
  try {
    return sessionStorage.getItem(SESSION_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeSessionMarker(participantId: string): void {
  try {
    sessionStorage.setItem(SESSION_MARKER_KEY, participantId);
  } catch {
    // Session continuity is best-effort when storage is unavailable.
  }
}

function safeNonNegativeInteger(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function isCurrencyCode(value: unknown): value is CurrencyCode {
  return value === "USD" || value === "UAH";
}

function safePositiveInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0 ? value : fallback;
}

function normalizeNickname(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.replace(/\s+/gu, " ").trim();
  if (!normalized) return null;
  return Array.from(normalized).slice(0, MAX_NICKNAME_LENGTH).join("");
}

function validIsoDate(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function removeStoredState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A storage failure must not block the experience.
  }
}

function requirePot(state: PersistedDemoState, potId: string): PotRecord {
  const pot = state.pots.find((candidate) => candidate.id === potId);
  if (!pot) throw new Error("Ritual pot not found.");
  return pot;
}

function createId(prefix: string): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${prefix}_${value}`;
}
